import * as React from 'react';
import { getInvites, InviteStatusFilter } from '../services/invites';
import { getInvitesColumns, saveInvitesColumns } from '@common/actions/invites';
import { ColumnListDto } from '@common/dto/column/list-dto';
import { NewTableLoadMetadata, NewTableSortingDirection } from '@common/components/new-table';
import { InviteListDto } from '@common/dto/invite/list-dto';
import { generateId } from '@common/utils/common';
import { restoreInvitesColumnsToDefault as restoreInvitesColumnsToDefaultBase, UpdateInviteStatusParams, updateInviteStatus, updateReviewedStatusActions } from '@common/actions/invites';
import { InviteReviewedStatusStateUpdateDto } from "@common/dto/invite-reviewed-status-state/update-dto";
import { InviteStatus } from "@common/dto/invite-status";
import { HttpErrorDto } from '@common/dto/http';
import { I18n } from 'react-redux-i18n';
import { InviteUpdateLockStatusParams, getFamilyIdMembersCount, updateLockStatus } from '@common/actions/individuals';
import { getFolderFields } from '@common/actions/formats';
import { FieldListDto } from '@common/dto/field/list-dto';
import { getModuleFolders } from '@common/actions/folders';
import { getQuestionnaires, launchNewIndividualQuestionnaire } from '@common/actions/questionnaires';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { QuestionnaireListDto } from '@common/dto/questionnaire/list-dto';
import { SkipLogicCondition } from '@common/components/skip-logic/skip-logic.types';

export type InvitesControllerProps = {
  showErrorMessage: (msg: string) => void;
  getColumnId: (col: ColumnListDto) => string;
};

export const useInvitesController = (props: InvitesControllerProps) => {
  const { getColumnId, showErrorMessage } = props;
  const INVITES_LOAD_ITEMS_COUNT = 50;
  const inviteItemUniqProperty = 'customInviteItemUniqProperty';

  const [invitesLoadMetadata, setInvitesLoadMetadata] = React.useState<NewTableLoadMetadata>({
    pagination: {
      size: INVITES_LOAD_ITEMS_COUNT,
      start: 0,
    },
  });
  const [invitesCount, setInvitesCount] = React.useState(0);
  const [invites, setInvites] = React.useState<Array<InviteListDto>>([]);
  const [invitesLoading, setInvitesLoading] = React.useState(false);
  const [invitesColumns, setInvitesColumns] = React.useState<Array<ColumnListDto>>([]);
  const [invitesSelectedItems, setInvitesSelectedItems] = React.useState<Array<InviteListDto>>([]);
  const [invitesSearchKeyword, setInvitesSearchKeyword] = React.useState('');

  const handleGetInvites = async (params: NewTableLoadMetadata & InviteStatusFilter & { columns: Array<ColumnListDto>; loadedInvites: Array<InviteListDto>; searchKeyword?: string ,clauses?:Array<SkipLogicCondition> }) => {
    try {
      setInvitesLoading(true);

      const { filtering, pagination, sorting } = params;

      const columns = params.columns.map((x): ColumnListDto => {
        const isSorted = getColumnId(x) === sorting?.id;
  
        return {
          ...x,
          sortOrder: isSorted ? 1 : 0,
          sortAsc: isSorted ? sorting?.direction === NewTableSortingDirection.asc : false,
        };
      });
  
      const filters = filtering?.columns || {};
      const columnFiltersProcessed = Object.keys(filters).map((columnId) => {
        const columnIndex = params.columns.findIndex((x) => getColumnId(x) == columnId);
        const columnToRemoveBecauseOfActionsColumn = 1;
        const value = filters[columnId];
  
        return {
          columnId: columnIndex + columnToRemoveBecauseOfActionsColumn,
          operator1: value?.operator1 ?? null,
          operator2: value?.operator2 ?? null,
          value1: value?.whereClause1 ?? null,
          value2: value?.whereClause2 ?? null,
          andOr: value?.andOR,
        };
      });

      const { data, totalCount } = await getInvites({
        pagination,
        filtering: {
          search: params.searchKeyword,
          filterApplied: filtering?.filter ?? false,
          columnFilters: columnFiltersProcessed,
          columns,
          questionnaires: params.questionnaires,
          inviteStatuses: params.inviteStatuses,
          invitedBy: params.invitedBy,
          dateSent: params.dateSent || undefined,
          dateStarted: params.dateStarted || undefined,
          dateCompleted: params.dateCompleted || undefined,
          clauses: params.clauses || [],
        },
      });

      const processedData = data.map((x) => {
        (x as Record<string, any>)[inviteItemUniqProperty] = generateId();
        return x;
      });

      const finalData = pagination.start >= pagination.size
        ? [...params.loadedInvites, ...processedData]
        : processedData;

      setInvites(finalData);
      setInvitesCount((x) => pagination.start > 0 ? x : totalCount);
      setInvitesLoadMetadata({
        pagination,
        sorting,
        filtering
      });
      setInvitesSearchKeyword(params.searchKeyword || '');
    } finally {
      setInvitesLoading(false);
    }
  };

  const updateInvitesColumns = React.useCallback(async (columns: Array<ColumnListDto>) => {
    await saveInvitesColumns(columns);
    setInvitesColumns(columns);
  }, []);

  const getAndSetInvitesColumns = React.useCallback(async () => {
    const cols = await getInvitesColumns();
    setInvitesColumns(cols);
    
    return cols;
  }, []);

  const restoreInvitesColumnsToDefault = React.useCallback(async () => {
    const cols = await restoreInvitesColumnsToDefaultBase();
    setInvitesColumns(cols);
    return cols;
  }, []);

  const onRefreshInvitesOrChangeInvitesColumns = () => {
    setInvitesSelectedItems([]);
  };

  const handleUpdateInviteStatus = async (data: UpdateInviteStatusParams) => {
    await updateInviteStatus(data);      

    handleGetInvites({
      columns: invitesColumns,
      loadedInvites: [],
      sorting: invitesLoadMetadata.sorting,
      filtering: invitesLoadMetadata.filtering,
      pagination: {
        start: 0,
        size: INVITES_LOAD_ITEMS_COUNT,
      },
    }).then(() => {
      onRefreshInvitesOrChangeInvitesColumns();
    }).catch((err) => {
      console.error(err);
      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get invites');
      showErrorMessage(errorMessage);
    });
  };

  const handleUpdateReviewedStatusActions = async ({ inviteIds, ...restData }: InviteReviewedStatusStateUpdateDto) => {
    const processedInviteIds = inviteIds.map((x) => ({ ...x, fhq4: x.fhq4 }));
    await handleUpdateInviteStatus({
      inviteIds: processedInviteIds,
      status: InviteStatus.reviewed,
    });
    
    await updateReviewedStatusActions({
      inviteIds: processedInviteIds,
      ...restData,
    });

    handleGetInvites({
      columns: invitesColumns,
      loadedInvites: [],
      sorting: invitesLoadMetadata.sorting,
      filtering: invitesLoadMetadata.filtering,
      pagination: {
        start: 0,
        size: INVITES_LOAD_ITEMS_COUNT,
      },
    }).then(() => {
      onRefreshInvitesOrChangeInvitesColumns();
    }).catch((err) => {
      console.error(err);
      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get invites');
      showErrorMessage(errorMessage);
    });
  };

  const handleInvitesColumnsOrderChange = async (columns: Array<ColumnListDto>) => {
    const currentSortingColumnExists = columns.some((x) => getColumnId(x) === invitesLoadMetadata?.sorting?.id);
    const newInvitesLoadMetadata: NewTableLoadMetadata = {
      sorting: currentSortingColumnExists 
        ? invitesLoadMetadata?.sorting!
        : undefined,
      pagination: {
        start: 0,
        size: INVITES_LOAD_ITEMS_COUNT,
      },
    };

    await updateInvitesColumns(columns);
    await getAndSetInvitesColumns();

    handleGetInvites({
      columns,
      loadedInvites: [],
      ...newInvitesLoadMetadata
    }).then(() => {
      onRefreshInvitesOrChangeInvitesColumns();
    }).catch((err) => {
      console.error(err);
      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get invites');
      showErrorMessage(errorMessage);
    });
  };

  const handleUpdateInvitesLockStatus = async (data: Array<InviteUpdateLockStatusParams>) => {
    await Promise.all(data.map(updateLockStatus));

    handleGetInvites({
      columns: invitesColumns,
      loadedInvites: [],
      sorting: invitesLoadMetadata.sorting,
      filtering: invitesLoadMetadata.filtering,
      pagination: {
        start: 0,
        size: INVITES_LOAD_ITEMS_COUNT,
      },
    }).then(() => {
      onRefreshInvitesOrChangeInvitesColumns();
    }).catch((err) => {
      console.error(err);
      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get invites');
      showErrorMessage(errorMessage);
    });
  };

  const handleRestoreInvitesColumnsToDefault = async () => {
    const columns = await restoreInvitesColumnsToDefault();

    handleGetInvites({
      columns,
      loadedInvites: [],
      sorting: undefined,
      filtering: undefined,
      pagination: {
        start: 0,
        size: INVITES_LOAD_ITEMS_COUNT,
      },
    }).then(() => {
      onRefreshInvitesOrChangeInvitesColumns();
    }).catch((err) => {
      console.error(err);
      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t('Failed to get invites');
      showErrorMessage(errorMessage);
    });
  };

  const getFieldsForInvitesColumn = React.useCallback((params: { searchQuery?: string; folderId?: FolderPrimaryListDto['id']; system: boolean }) => {
    const individualsContext = 1;
    
    return getFolderFields({
      searchQuery: params.searchQuery,
      folderId: params.folderId,
      contextId: individualsContext,
      includeTables: false,
    }).then((fields) => fields.map((field) => ({
      fieldId: field.id,
      fieldName: field.name,
      fieldContext: field.context,
      fieldTypeId: field.type,
      systemField: field.system,
      imageURL: field.imageURL,
      fullPath: field.fullPath,
      options: field.options,
    })) as unknown as FieldListDto[]);
  }, []);

  const getFoldersForInvitesColumn = React.useCallback(() => {
    const individualsId = 1;

    return getModuleFolders({ folderTypeId: individualsId, setUp: { rootFolderImgUrl: 'gif/df_individual.gif', rootFolderName: I18n.t('Individual Data Folders') } });
  }, []);

  const getQuestionnairesForNewQuestionnaire = React.useCallback(async (familyId: string) => {
    const membersCount = await getFamilyIdMembersCount(familyId);
    const isSinglet = invites.find((x) => x.familyId === familyId)?.individualType === 'true';
    const questionnaires = await getQuestionnaires({
      includeFamilyQuestionnaires: !isSinglet && membersCount <= 1,
      includeFhq4Questionnaires: true,
      includeHiddenQuestionnaires: false
    });

    return questionnaires;
  }, [invites]);

  const handleLaunchNewQuestionnaire = React.useCallback(async (globalId: string, questionnaire: QuestionnaireListDto | undefined) => {
    const redirectUrl = await launchNewIndividualQuestionnaire(
      globalId, 
      questionnaire?.questionnaireId,
      questionnaire?.fhq4
    );
    window.location.href = redirectUrl;
  }, []);

  const handleInviteSelectedItemsChange = React.useCallback((invites: InviteListDto[]) => {
    setInvitesSelectedItems(invites);
  }, []);

  return {
    INVITES_LOAD_ITEMS_COUNT,
    handleInviteSelectedItemsChange,
    handleLaunchNewQuestionnaire,
    getQuestionnairesForNewQuestionnaire,
    getFoldersForInvitesColumn,
    getFieldsForInvitesColumn,
    handleRestoreInvitesColumnsToDefault,
    handleUpdateInvitesLockStatus,
    handleInvitesColumnsOrderChange,
    onRefreshInvitesOrChangeInvitesColumns,
    handleUpdateInviteStatus,
    handleUpdateReviewedStatusActions,
    invitesSearchKeyword,
    inviteItemUniqProperty,
    invitesLoadMetadata,
    invites,
    invitesLoading,
    invitesCount,
    handleGetInvites,
    invitesSelectedItems,
    invitesColumns,
    getAndSetInvitesColumns,
    updateInvitesColumns,
  };
};